import type { ReactNode } from 'react';
import { match } from 'ts-pattern';

import { CheckIcon, ReportIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';

import { LoadingSpinner } from '../LoadingSpinner';

export type ProcessingStatus =
  | 'wait-for-approval'
  | 'pending'
  | 'success'
  | 'error'
  | 'review'
  | 'partial';

export type ProcessingStatusCardProps = {
  status: ProcessingStatus;
  statusLabels: Record<
    Exclude<ProcessingStatus, 'wait-for-approval' | 'review' | 'partial'>,
    string
  > & {
    'wait-for-approval'?: string;
    review?: string;
    partial?: string;
  };
  children: ReactNode;
  'data-testid'?: string;
  className?: string;
  contentClassName?: string;
  /** Extra classes for the status header text (e.g. a tone override). */
  headerClassName?: string;
};

/**
 * A card component for displaying processing status with a colored border.
 *
 * The status indicator (icon + text) is displayed at the top,
 * with the children (typically asset details) rendered below.
 */
export const ProcessingStatusCard = ({
  status,
  statusLabels,
  children,
  'data-testid': testId,
  className,
  contentClassName,
  headerClassName,
}: ProcessingStatusCardProps) => {
  return (
    <div
      data-testid={testId}
      className={cn(
        // overflow-hidden backstops inner content from painting over the
        // ring — content whose corner radius is too small would otherwise
        // poke past the outer arc at the corners.
        'overflow-hidden rounded-2xl p-0.5',
        {
          'bg-surfaceTwo': status === 'wait-for-approval',
          'bg-fillInfo': status === 'pending',
          'bg-fillPositive': status === 'success' || status === 'partial',
          'bg-fillCritical': status === 'error',
          'bg-surface': status === 'review',
        },
        className
      )}
    >
      <div className="flex items-center justify-center gap-x-2 py-2">
        {match(status)
          .with('wait-for-approval', () => null)
          .with('pending', () => (
            <LoadingSpinner size="small" prominence="info" />
          ))
          .with('success', () => <CheckIcon fill="positive" size="2xsmall" />)
          .with('partial', () => <CheckIcon fill="positive" size="2xsmall" />)
          .with('error', () => (
            <ReportIcon className="fill-critical" size="small" />
          ))
          .with('review', () => null)
          .exhaustive()}

        <h2
          className={cn(
            'text-sm font-medium',
            {
              'text-tertiary': status === 'wait-for-approval',
              'text-info': status === 'pending',
              'text-positive': status === 'success',
              'text-critical': status === 'error',
              'text-secondary': status === 'review',
            },
            headerClassName
          )}
        >
          {match(status)
            .with(
              'wait-for-approval',
              () => statusLabels['wait-for-approval'] || ''
            )
            .with('pending', () => statusLabels.pending)
            .with('success', () => statusLabels.success)
            .with('partial', () => statusLabels.partial)
            .with('error', () => statusLabels.error)
            .with('review', () => statusLabels.review)
            .exhaustive()}
        </h2>
      </div>

      <div
        className={cn(
          'bg-surfaceAlt flex gap-2 rounded-xl rounded-b-2xl p-4',
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  );
};

type ProcessingAssetDisplayProps = {
  assetIcon: ReactNode;
  assetName: string;
  chainLabel: string;
  amount: ReactNode;
  usdDisplay: ReactNode;
};

/**
 * Asset display for use within ProcessingStatusCard.
 * Shows asset icon, name, chain, amount, and USD value.
 */
export const ProcessingAssetDisplay = ({
  assetIcon,
  assetName,
  chainLabel,
  amount,
  usdDisplay,
}: ProcessingAssetDisplayProps) => {
  return (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-x-3">
        {assetIcon}

        <div className="flex min-w-0 flex-col">
          <span className="text-emphasis truncate text-lg font-semibold">
            {assetName}
          </span>

          <span className="text-secondary truncate text-xs font-medium">
            {chainLabel}
          </span>
        </div>
      </div>

      <div className="flex flex-none flex-col items-end">
        <div className="text-emphasis text-lg font-semibold">{amount}</div>

        <div className="flex gap-1 text-xs font-medium">{usdDisplay}</div>
      </div>
    </>
  );
};
